/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Payload, AnalyticsConstants, VU, Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { SessionErrorResponseCodeEnum } from 'client-code/session-manager';
import { AiringOffer } from 'client-code/airing';
import { ScreenType } from 'client-code/previews';
import { TOfferPreviewCustomData } from '../../core/types';
import { RootState } from '../../store/root.reducers';
import { Spinner } from './Spinner';
import { ActionsStrip } from './ActionsStrip';
import {
    EMPTY_STRING,
    INFO_BANNER_HIDE_ANIMATION_TIMEOUT_VALUE,
    LIVE_TV_CANNOT_PLAY_ON_THIS_DEVICE_OSD_ERROR_CODE,
    LIVE_TV_NO_NETWORK_ERROR_PRIORITY,
    LIVE_TV_OUT_OF_REGION_OSD_ERROR_CODE,
    NO_NETWORK_OVERLAY_ERROR_CODE,
} from '../../config';
import { StandardInfoBanner } from './StandardInfoBanner';
import { EventDescription } from './FullInfoBanner/EventDescription';
import { SpinnerPositionTypes } from './Spinner/SpinnerPositionTypes';
import { Osd } from './OSD';
import { OsdTypes } from './OSD/OSDTypes';
import { InfoBannerUtils } from './FullInfoBanner/EventDescription/InfoBannerUtils';
import { TrickplayBanner } from './Trickplay';
import { Locale } from '../../core/locale/Locale';
import { LocaleKeys } from '../../core/locale/LocaleKeys';
import {
    hideInfoBanners,
    hideTrickPlayMenu,
    showStandardInfo,
    showTrickPlayMenu,
    showTrickPlayMenuOnStartUp,
    updateTrickPlayMenuTimer,
} from '../../store/videoplayerui/infobanner/infobanner.reducers';
import {
    disableStillThereTimer,
    resetStillThereTimeout,
    toggleIsPlaying,
} from '../../store/videoplayer/videoplayer.reducers';
import { Storage } from '../../core/storage/Storage';
import { Log } from '../../core/logging/Log';
import { OperationsLogger, QoEAppLogger } from '../../core/logging';
import { BrandingUiInfoKeys } from '../../core/entities/BrandingUiInfoKeys';
import { VisibilityState } from '../../store/visibilityevents/visibilitystate';
import { PlaybackError } from '../../core/error/PlaybackError';
import { ErrorTypes } from '../../core/error/ErrorTypes';
import { VideoDebugOverlay } from './VideoDebugOverlay';
import { PlaybackState } from '../../store/videoplayerui/infobanner/PlaybackTypes';
import {
    videoPlayerToggleIsPlayingAction,
    videoPlayerSetPlaybackStateAction,
} from '../../store/videoplayer/videoplayer.actions';
import { VuLogger } from '../../core/logging/VULogger';
import { NetworkManager } from '../../core/network/NetworkManager';
import CursorManager from '../../core/navigation/CursorManager';
import { overlayDisableGhostMode } from '../../store/overlay/overlayhighlight/overlayhighlight.reducers';
import {
    clearDebounceTimerVideoPlayerUi,
    swtichToPreviousControllerFromInteractiveLayer,
} from '../../store/videoplayerui/videoplayeruinavigation.reducers';
import { RootInteractiveLayer } from './InteractiveLayer';
import { showSpinner, hideSpinner } from '../../store/spinner/spinner.reducers';
import { setCurrentActiveIndexAction } from '../../store/videoplayerui/infobanner/infobanner.actions';
import { hideErrorOSD, showErrorOSD } from '../../store/videoplayerui/osd/osd.reducers';
import { SpinnerDelays } from '../../core/enums/SpinnerDelays';
import { QoEConnectionModeType } from '../../core/logging/qoe/QoEConnectionModeType';
import { MediaButtonTypes } from '../../store/videoplayerui/infobanner/mediabuttontypes';
import { hideOverlayDialog } from '../../store/overlaydialog/overlaydialog.reducers';
import { OverlayTypes } from '../../store/overlay/OverlayTypes';
import { DeviceFeatures, DeviceModel } from '../../core/device';
import { VideoManager } from '../../core/videoplayer';
import { VideoPlayerModel } from '../../core/videoplayer/interfaces';
import { SocuDataModelImpl } from '../../core/socu/impls';
import {
    MetadataState,
    PlaybackRestrictionType,
    VideoPlayerStates,
} from '../../core/videoplayer/enums';
import { IpLinearStreamingSessionImpl } from '../../core/videoplayer/impls';
import { LiveTvPlaybackError } from '../../core/videoplayer/error';
import { ServicesClientImpl } from '../../core/impls';

import './VideoPlayerUI.scss';
import { PlaybackContentType } from '../../core/socu/enums/PlaybackContentType';

const VideoPlayerUI: FunctionComponent = (): JSX.Element => {
    const TAG: string = 'VideoPlayerUI';
    const dispatch = useDispatch();
    const oIVideoPlayer: VideoPlayerModel = VideoManager.getInstance().getVideoPlayerModel();
    const services = new ServicesClientImpl();
    const isTizenPlatform = DeviceModel.isTizenPlatform();
    let showOsd = false;

    // useSelectors
    const { isErrorOSDActive, errorCode, errorMessage, errorPriority } = useSelector(
        (state: RootState) => state.errorOsd,
    );
    const { isOverlayDialogActive, uiProperties } = useSelector(
        (state: RootState) => state.overlayDialog,
    );
    const { isOverrideEnable, overrideDetails } = useSelector((state: RootState) => state.appl);
    const { isPlaying, channel, playbackState } = useSelector(
        (state: RootState) => state.videoPlayer,
    );
    const { isSpinnerActive, spinnerMessage, errorCodes } = useSelector(
        (state: RootState) => state.spinner,
    );
    const { isStillThereOsdActive, isStillThere } = useSelector(
        (state: RootState) => state.videoPlayer,
    );
    const { isSubscribed, policyInfo, name, channelNumber } = channel;
    const { isVideoDebugOverlayActive } = useSelector(
        (state: RootState) => state.videoDebugOverlay,
    );
    const { playerState } = useSelector((state: RootState) => state.videoPlayerState);
    const { tuningUri, errorCode: overrideErrorCode } = overrideDetails;
    const isActionsStripActive = useSelector(
        (state: RootState) => state.infoBanner.isActionsStripActive,
    );
    const isFullInfoActive = useSelector((state: RootState) => state.infoBanner.isFullInfoActive);
    const isStandardInfoActive = useSelector(
        (state: RootState) => state.infoBanner.isStandardInfoActive,
    );
    const isTrickplayMenuActive = useSelector(
        (state: RootState) => state.infoBanner.isTrickplayMenuActive,
    );
    const visibilityState = useSelector(
        (state: RootState) => state.visibilityEvents.visibilityState,
    );

    // useStates
    const [actionsStripAnimationWait, setActionsStripAnimationWait] = useState(false);
    const [currentAudioTrackSelected, setCurrentAudioTrackSelected] = useState('');
    const [currentTextTrack, setCurrentTextTrack] = useState('');
    const [fullInfoAnimationWait, setFullInfoAnimationWait] = useState(false);
    const [isCursorEnabled, setIsCursorEnabled] = useState(CursorManager.isCursorAppeared());
    const [isNetworkConnected, setIsNetworkConnected] = useState(true);
    const [metadataState, setMetadataState] = useState(MetadataState.PREPARING);
    const [isFullInfoDataFetched, setisFullInfoDataFetched] = useState<boolean>(false);
    const [airingOffers, setAiringOffers] = useState<AiringOffer[]>(
        VideoManager.getInstance().getMetadata().airingOffers || [],
    );
    const [offerPreview, setOfferPreview] = useState<TOfferPreviewCustomData>(
        VideoManager.getInstance().getMetadata().offerPreview || {},
    );
    const [playbackRestrictionType, setPlaybackRestrictionType] = useState(
        VideoManager.getInstance().getPlaybackRestrictionType(),
    );
    const [standardInfoAnimationWait, setStandardInfoAnimationWait] = useState(false);
    const [trickplayAnimationWait, setTrickplayAnimationWait] = useState(false);
    const [hideBannerTipAtProgramBoundary, setHideBannerTipAtProgramBoundary] = useState(false);

    // useRefs
    const isScreenActive = React.useRef(false);
    const onceOnVideoLaunch = useRef(false);
    const prevNetworkState = useRef(true);
    const screenEntryTime = useRef(0);

    const isDeviceRestricted: boolean =
        playbackRestrictionType === PlaybackRestrictionType.DeviceRestricted;
    const isDeviceOutOfHome: boolean =
        playbackRestrictionType === PlaybackRestrictionType.OutOfHome;

    const dataFormatter = new InfoBannerUtils(airingOffers, offerPreview);

    const infoBannerDivAnimationWait =
        !isTizenPlatform &&
        (standardInfoAnimationWait || actionsStripAnimationWait || fullInfoAnimationWait);
    let isAdult;
    if (airingOffers) isAdult = airingOffers[0]?.parentalControls?.isAdult;
    const isDeviceOutOfRegion: boolean =
        playbackRestrictionType === PlaybackRestrictionType.OutOfRegion;
    const isErrorOverlayActive =
        isOverlayDialogActive && uiProperties.overlayType === OverlayTypes.ERROR;
    const isInBlacklist: boolean = playbackRestrictionType === PlaybackRestrictionType.Blacklisted;
    const isInfobannerContainerActive =
        isActionsStripActive ||
        isFullInfoActive ||
        isStandardInfoActive ||
        isTrickplayMenuActive ||
        (isNetworkConnected && oIVideoPlayer.isTrickplayEnable(playbackState));
    const isNetworkOsdActive: boolean =
        isErrorOSDActive && errorPriority === LIVE_TV_NO_NETWORK_ERROR_PRIORITY;
    const isSubtitlesCCLanguageVisible: boolean =
        !!currentTextTrack && oIVideoPlayer.isTextTrackChangeSupported();
    let osdCode = EMPTY_STRING;
    let osdMessage = EMPTY_STRING;
    let osdType = OsdTypes.DEFAULT;

    const actionItems = useMemo(() => {
        return [
            {
                name: Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_RESUME),
                visible: !isPlaying && !isNetworkOsdActive,
            },
            {
                name: Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_CHANGE_AUDIO_TRACK),
                message: currentAudioTrackSelected
                    ? `${Locale.getString(
                          LocaleKeys.LIVE_TV_CHANGE_AUDIO_TRACK_MESSAGE,
                      )}  ${currentAudioTrackSelected}`
                    : '',
                visible: true,
            },
            {
                name: services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER),
                visible:
                    DeviceFeatures.isSocuEnabled() &&
                    dataFormatter.getIsAvailableInSocu() &&
                    isSubscribed,
            },
            {
                name: `${
                    oIVideoPlayer.getClosedCaptionsStatus()
                        ? Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_TURN_SUBTITLES_CC_OFF)
                        : Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_TURN_SUBTITLES_CC_ON)
                }`,
                message: `${Locale.getString(LocaleKeys.LIVE_TV_TURN_SUBTITLES_CC_ON_MESSAGE)}
                 ${
                     oIVideoPlayer.getClosedCaptionsStatus()
                         ? Locale.getString(LocaleKeys.LIVE_TV_ON)
                         : Locale.getString(LocaleKeys.LIVE_TV_OFF)
                 }`,
                visible: oIVideoPlayer.isTextTrackSupported(),
            },
            {
                name: Locale.getString(
                    LocaleKeys.LIVE_TV_ACTION_STRIP_CHANGE_SUBTITLES_CC_LANGUAGE,
                ),
                message: `${Locale.getString(
                    LocaleKeys.LIVE_TV_CHANGE_SUBTITLES_CC_LANGUAGE_MESSAGE,
                )}  ${currentTextTrack}`,
                visible: isSubtitlesCCLanguageVisible && oIVideoPlayer.getClosedCaptionsStatus(),
            },
            {
                name: Locale.getString(LocaleKeys.LIVE_TV_STREAM_STATS),
                message: Locale.getString(LocaleKeys.LIVE_TV_STREAM_STATS),
                visible: Storage.getInstance().getItem(Storage.Keys.IS_VIDEO_DEBUG_OVERLAY_ACTIVE),
            },
        ];
    }, [
        currentAudioTrackSelected,
        isPlaying,
        isNetworkOsdActive,
        dataFormatter.getIsAvailableInSocu(),
        oIVideoPlayer.isTextTrackSupported(),
        isSubtitlesCCLanguageVisible,
        currentTextTrack,
        oIVideoPlayer.getClosedCaptionsStatus(),
    ]);

    const handlePlaybackRestriction = (type: PlaybackRestrictionType) => {
        setPlaybackRestrictionType(type);
        switch (type) {
            case PlaybackRestrictionType.Blacklisted:
                OperationsLogger.logUIErrorCode(ErrorTypes.LIVE_TV_IN_BLACKLIST_OSD_ERROR_C138);
                break;
            case PlaybackRestrictionType.DeviceRestricted:
                OperationsLogger.logUIErrorCode(
                    ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_AUTHORIZED_DEVICE_TYPE_V414,
                );
                break;
            case PlaybackRestrictionType.InHomeLocationError:
                // TODO: Need to be refactor with Playback error module.
                VideoManager.getInstance()
                    .getVideoPlayerModel()
                    .handleLocationError(
                        ErrorTypes.LIVE_TV_LOCATION_RESTRICTION_OSD_ERROR_CODE_V418,
                    );
                break;
            case PlaybackRestrictionType.InHomeLocationUnreachable:
                // TODO: Need to be refactor with Playback error module.
                VideoManager.getInstance()
                    .getVideoPlayerModel()
                    .handlePlaybackError(ErrorTypes.LIVE_TV_GRACE_PERIOD_ENDED_ERROR_C718);
                break;
            case PlaybackRestrictionType.OutOfHome:
                OperationsLogger.logUIErrorCode(ErrorTypes.LIVE_TV_OOH_OSD_ERROR_V404);
                break;
            case PlaybackRestrictionType.OutOfRegion:
                OperationsLogger.logUIErrorCode(ErrorTypes.LIVE_TV_OUT_OF_REGION_OSD_ERROR_V405);
                break;

            default:
        }
    };

    const handleMetadataStateChange = (state: MetadataState) => {
        switch (state) {
            case MetadataState.AIRING_READY:
                onAiringOffersReady();
                break;
            case MetadataState.PREVIEW_READY:
                setOfferPreview(VideoManager.getInstance().getMetadata().offerPreview!);
                break;
            case MetadataState.PREVIEW_ERROR:
                setOfferPreview({});
                break;
            case MetadataState.VOD_PREVIEW_READY:
                setAiringOffers([]);
                setOfferPreview(VideoManager.getInstance().getMetadata().offerPreview!);
                break;
            case MetadataState.VOD_PREVIEW_ERROR:
                setAiringOffers([]);
                setOfferPreview({});
                break;
            case MetadataState.AIRING_ERROR:
                onAiringOffersError();
                setisFullInfoDataFetched(false);
                break;
            default:
        }
        setMetadataState(state);
    };

    const onAiringOffersReady = () => {
        if (isOverlayDialogActive) dispatch(hideOverlayDialog());
        setAiringOffers(VideoManager.getInstance().getMetadata().airingOffers!);
        setisFullInfoDataFetched(false);
    };

    const onAiringOffersError = () => {
        dispatch(hideSpinner());
        // TODO: We should avoid use of `handlePlaybackError` outside VideoPlayerModelImpl.
        VideoManager.getInstance()
            .getVideoPlayerModel()
            .handlePlaybackError(ErrorTypes.LIVE_TV_AIRING_DATA_FAILED_V139);
        Diagnostics.error(TAG, 'Airing data fail scenario');
        Log.e(TAG, 'Airing data fail scenario');
    };

    const onNetworkChangeCallback = useCallback((state) => {
        if (
            state === NetworkManager.NetworkState.GATEWAY_CONNECTED ||
            state === NetworkManager.NetworkState.INTERNET_CONNECTED
        ) {
            setIsNetworkConnected(true);
        } else if (
            state === NetworkManager.NetworkState.GATEWAY_DISCONNECTED ||
            state === NetworkManager.NetworkState.INTERNET_DISCONNECTED
        ) {
            setIsNetworkConnected(false);
        }
    }, []);

    const onCursorStateChangeCallback = (state) => {
        setIsCursorEnabled(state);
        if (!state) {
            dispatch(swtichToPreviousControllerFromInteractiveLayer());
            dispatch(overlayDisableGhostMode());
        }
    };

    const handleOnProgramBoundary = () => {
        setHideBannerTipAtProgramBoundary(true);
        if (!(isFullInfoActive || isStandardInfoActive || isTrickplayMenuActive))
            dispatch(showStandardInfo());
    };

    /**
     * This UseEffect will be called for change in player state.
     * It is used to update the currentAudioTrackSelected state so it updates everytime in
     * ActionsStrip whenever current selected audio track is changed on player Loaded.
     */
    useEffect(() => {
        if (playerState === VideoPlayerStates.LOADED)
            setCurrentAudioTrackSelected(oIVideoPlayer.getCurrentAudioSelectedTrack());
    }, [playerState]);

    useEffect(() => {
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        const refNetworkManager = NetworkManager.subscribe(onNetworkChangeCallback);
        const refPlaybackRestrictionHandler =
            VideoManager.getInstance().addPlaybackRestrictionListener(handlePlaybackRestriction);
        const refProgramBoundaryHandler =
            VideoManager.getInstance().addProgramBoundaryListener(handleOnProgramBoundary);
        const payload: Payload = new Payload();
        screenEntryTime.current = Date.now();
        payload.add(
            AnalyticsConstants.Fields.SCREEN_NAME,
            AnalyticsConstants.Screens.LIVE_TV_SCREEN,
        );
        payload.add(
            AnalyticsConstants.Fields.SCREEN_TRANSITION,
            AnalyticsConstants.TransitionState.ENTER,
        );
        VU.screen(payload);
        const refMetadataStateChangeHandler =
            VideoManager.getInstance().addMetadataStateChangeListener(handleMetadataStateChange);

        return () => {
            CursorManager.unsubscribe(refCursorManager);
            NetworkManager.unsubscribe(refNetworkManager);
            VideoManager.getInstance().removePlaybackRestrictionListner(
                refPlaybackRestrictionHandler,
            );
            VideoManager.getInstance().removeProgramBoundaryListener(refProgramBoundaryHandler);
            window.removeEventListener('online', () => true);
            window.removeEventListener('offline', () => true);
            clearDebounceTimerVideoPlayerUi();
            payload.add(
                AnalyticsConstants.Fields.SCREEN_TRANSITION,
                AnalyticsConstants.TransitionState.EXIT,
            );
            payload.add(
                AnalyticsConstants.Fields.TIME_SPENT_ON_SCREEN,
                Date.now() - screenEntryTime.current,
            );
            VU.screen(payload);
            VideoManager.getInstance().removeMetadataStateChangeListner(
                refMetadataStateChangeHandler,
            );
        };
    }, []);

    // Hide OSD on component unmout.
    useEffect(() => {
        return () => {
            showOsd = false;
            dispatch(hideErrorOSD());
        };
    }, [channel, visibilityState]);

    // useEffect to refresh the Metadata for standard & fullinfo in case of SOCU.
    useEffect(() => {
        if (
            SocuDataModelImpl.getInstance().isSocu() &&
            isFullInfoActive &&
            !isFullInfoDataFetched
        ) {
            VideoManager.getInstance().refreshMetadata(
                oIVideoPlayer.getCurrentTimeInMs(),
                ScreenType.FullBanner,
            );
            setisFullInfoDataFetched(true);
        }
    }, [isFullInfoActive, metadataState]);

    // useEffect to manage playback limitations such as subscription and adult.
    useEffect(() => {
        if (playbackRestrictionType !== PlaybackRestrictionType.NoRestriction) showOsd = true;
    }, [playbackRestrictionType]);

    useEffect(() => {
        if (visibilityState === VisibilityState.ON_PAUSE) {
            onceOnVideoLaunch.current = false;
            showOsd = false;
        }
        if (isScreenActive && isScreenActive.current) {
            const payload: Payload = new Payload();
            payload.add(
                AnalyticsConstants.Fields.SCREEN_NAME,
                AnalyticsConstants.Screens.LIVE_TV_SCREEN,
            );
            if (visibilityState === VisibilityState.ON_RESUME) {
                screenEntryTime.current = Date.now();
                payload.add(
                    AnalyticsConstants.Fields.SCREEN_TRANSITION,
                    AnalyticsConstants.TransitionState.RESUME,
                );
            } else if (visibilityState === VisibilityState.ON_PAUSE) {
                payload.add(
                    AnalyticsConstants.Fields.SCREEN_TRANSITION,
                    AnalyticsConstants.TransitionState.SUSPEND,
                );
                payload.add(
                    AnalyticsConstants.Fields.TIME_SPENT_ON_SCREEN,
                    Date.now() - screenEntryTime.current,
                );
            }
            VU.screen(payload);
        }

        if (NetworkManager.isNetworkDisconnected()) {
            dispatch(
                showErrorOSD(
                    NO_NETWORK_OVERLAY_ERROR_CODE,
                    Locale.getString(LocaleKeys.NO_NETWORK_OVERLAY_DESCRIPTION),
                    LIVE_TV_NO_NETWORK_ERROR_PRIORITY,
                ),
            );
        }
        return () => {
            isScreenActive.current = true;
            dispatch(hideSpinner());
        };
    }, [visibilityState]);

    useEffect(() => {
        if (!isNetworkConnected) {
            Diagnostics.info(TAG, 'Network lost, network osd shown');
            Log.i(TAG, 'Network lost, network osd shown');
            QoEAppLogger.updateConfigurationForConnectionMode(QoEConnectionModeType.OFFLINE);
            if (isOverlayDialogActive) {
                dispatch(hideOverlayDialog());
            }
            dispatch(
                showErrorOSD(
                    NO_NETWORK_OVERLAY_ERROR_CODE,
                    Locale.getString(LocaleKeys.NO_NETWORK_OVERLAY_DESCRIPTION),
                    LIVE_TV_NO_NETWORK_ERROR_PRIORITY,
                ),
            );
            QoEAppLogger.stopMediaHealthTimer();
            QoEAppLogger.logNetworkLost();
            VuLogger.logNotifyEvent(
                VuLogger.NotifyEventSourceType.NO_NETWORK_CONNECTION_OSD,
                VuLogger.NotifyEventSourceName.NO_NETWORK_CONNECTION,
            );
            dispatch(hideTrickPlayMenu());
            prevNetworkState.current = false;
        } else if (!prevNetworkState.current && isNetworkConnected) {
            Diagnostics.info(TAG, 'Network is connected, network osd hidden');
            Log.i(TAG, 'Network is connected, network osd hidden');
            dispatch(hideErrorOSD());
            dispatch(resetStillThereTimeout(true));
            Diagnostics.info(TAG, 'PlayerState after network gets back = ', playerState);
            Log.i(TAG, 'playerState after network gets back = ', playerState);
            if (!VideoManager.getInstance().getIsPlayerLoaded()) {
                dispatch(
                    showSpinner(
                        Locale.getString(LocaleKeys.LIVE_TV_SPINNER_LOADING_VIDEO),
                        SpinnerDelays.LIVE_TV,
                    ),
                );
                const currentTime = oIVideoPlayer.getCurrentTimeInMs() || new Date().getTime();
                if (currentTime > dataFormatter.getEndTime().getTime())
                    VideoManager.getInstance().restorePlayback();
                dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.PLAY));
                dispatch(videoPlayerToggleIsPlayingAction(true));
            } else {
                VuLogger.logStreamingRecovery(VuLogger.StreamingRecoveryType.NETWORK_ERROR);
                if (oIVideoPlayer.isTrickplayEnable(playbackState)) {
                    dispatch(showTrickPlayMenu());
                    if (
                        playbackState === PlaybackState.FF1 ||
                        playbackState === PlaybackState.FF2 ||
                        playbackState === PlaybackState.FF3
                    ) {
                        dispatch(
                            setCurrentActiveIndexAction(MediaButtonTypes.FORWARD_BUTTON_INDEX),
                        );
                    } else {
                        dispatch(setCurrentActiveIndexAction(MediaButtonTypes.REWIND_BUTTON_INDEX));
                    }
                    oIVideoPlayer.selectSpeed(playbackState);
                }
                // checkDeviceRestriction();
                const currentTime = oIVideoPlayer.getCurrentTimeInMs() || new Date().getTime();
                if (currentTime > dataFormatter.getEndTime().getTime()) {
                    VideoManager.getInstance().restorePlayback();
                } else {
                    const playable = VideoManager.getInstance().getPlayable();
                    QoEAppLogger.logWatchContentStart(
                        playable?.playbackContentType ?? PlaybackContentType.LIVE,
                    );
                    QoEAppLogger.logStreamingStartEvent();
                    QoEAppLogger.startMediaHealthTimer();
                }
            }
        }
    }, [isNetworkConnected]);

    useEffect(() => {
        if (visibilityState === VisibilityState.ON_RESUME) {
            if (isInBlacklist)
                VuLogger.logNotifyEvent(
                    VuLogger.NotifyEventSourceType.BLACKLISTED_OSD,
                    VuLogger.NotifyEventSourceName.BLACKLISTED,
                );
            else if (isDeviceOutOfRegion) {
                VuLogger.logNotifyEvent(
                    VuLogger.NotifyEventSourceType.OUT_OF_REGION_OSD,
                    VuLogger.NotifyEventSourceName.OUT_OF_REGION,
                );
            }
        }
    }, [isDeviceOutOfRegion, visibilityState]);

    useEffect(() => {
        if (visibilityState === VisibilityState.ON_RESUME) {
            if (isDeviceOutOfHome)
                VuLogger.logNotifyEvent(
                    VuLogger.NotifyEventSourceType.OUT_OF_HOME_OSD,
                    VuLogger.NotifyEventSourceName.OUT_OF_HOME,
                );
            else if (isDeviceRestricted) {
                VuLogger.logNotifyEvent(
                    VuLogger.NotifyEventSourceType.DEVICE_TYPE_RESTRICTED_OSD,
                    VuLogger.NotifyEventSourceName.DEVICE_TYPE_RESTRICTED,
                );
            }
        }
    }, [policyInfo, visibilityState]);

    useEffect(() => {
        Log.i(TAG, 'OverrideEnable: ', isOverrideEnable);

        if (isOverrideEnable && tuningUri) {
            channel.tuningUri = [tuningUri];
        }
        if (isOverrideEnable && overrideErrorCode) {
            const playerErrors = [
                'V959',
                'V958',
                'V957',
                'V956',
                'V955',
                'V954',
                'V953',
                'V952',
                'V951',
                'V960',
                'V934',
                'V909',
                'V908',
                'V904',
                'V117',
            ];
            const locationErrors = ['V418'];
            const error: any = {
                response: {
                    data: {
                        code: SessionErrorResponseCodeEnum.BadArgument,
                        message: 'Session error message',
                    },
                },
            };

            if (playerErrors.includes(overrideErrorCode)) {
                IpLinearStreamingSessionImpl.getInstance().handlePlaybackError(overrideErrorCode);
            } else if (locationErrors.includes(overrideErrorCode))
                // TODO: ANIKUMAR - Why this method has been used outside video model?
                oIVideoPlayer.handleLocationError(overrideErrorCode);
            else {
                switch (overrideErrorCode) {
                    case 'V410':
                        error.response.data.code = SessionErrorResponseCodeEnum.NotAuthorized;
                        break;
                    case 'V409':
                        error.response.data.code = SessionErrorResponseCodeEnum.NotAuthorized;
                        break;
                    case 'V408':
                        error.response.data.code =
                            SessionErrorResponseCodeEnum.TooManyStreamsForResource;
                        break;
                    case 'V407':
                        error.response.data.code = SessionErrorResponseCodeEnum.TooManyStreams;
                        break;
                    case 'V406':
                        error.response.data.code = SessionErrorResponseCodeEnum.NotRegistered;
                        break;
                    case 'V405':
                        error.response.data.code =
                            SessionErrorResponseCodeEnum.NotAuthorizedOutOfRegion;
                        break;
                    case 'V404':
                        error.response.data.code =
                            SessionErrorResponseCodeEnum.NotAuthorizedOutOfHome;
                        break;
                    case 'V403':
                        error.response.data.code = SessionErrorResponseCodeEnum.NotAuthenticated;
                        break;
                    case 'V138':
                        error.response.data.code = SessionErrorResponseCodeEnum.InternalError;
                        break;
                    case 'V137':
                        error.response.data.code = SessionErrorResponseCodeEnum.BadArgument;
                        break;
                    default:
                        break;
                }
                IpLinearStreamingSessionImpl.getInstance().handleSessionError(error);
            }
        }
    }, [isOverrideEnable, channel]);

    useEffect(() => {
        if (channel && Object.keys(channel).length && airingOffers?.length) {
            const isNotPlaybackError =
                !isInBlacklist &&
                !isDeviceOutOfRegion &&
                !isDeviceOutOfHome &&
                !isDeviceRestricted &&
                isSubscribed &&
                !isAdult &&
                !isErrorOSDActive &&
                !isErrorOverlayActive;

            if (!isNotPlaybackError) {
                dispatch(disableStillThereTimer());
            } else if (!isStillThere && playbackState === PlaybackState.PLAY) {
                dispatch(resetStillThereTimeout(true));
            }
        }
    }, [
        isInBlacklist,
        isDeviceOutOfRegion,
        isDeviceOutOfHome,
        isDeviceRestricted,
        isSubscribed,
        isErrorOSDActive,
        isErrorOverlayActive,
        metadataState,
        isAdult,
        channel,
    ]);

    useEffect(() => {
        const state = VideoManager.getInstance().getMetadataState();
        const isLiveMetadata =
            state === MetadataState.AIRING_READY ||
            state === MetadataState.PREVIEW_READY ||
            state === MetadataState.PREVIEW_ERROR;
        if (isLiveMetadata) {
            if (!onceOnVideoLaunch.current) {
                onceOnVideoLaunch.current = true;
                dispatch(showTrickPlayMenuOnStartUp());
            } else if (!(isFullInfoActive || isStandardInfoActive || isTrickplayMenuActive))
                dispatch(showStandardInfo());
        } else {
            dispatch(showTrickPlayMenu());
            onceOnVideoLaunch.current = false;
        }
    }, [metadataState]);

    useEffect(() => {
        if (!isTizenPlatform) {
            if (isTrickplayMenuActive) {
                setFullInfoAnimationWait(false);
                setActionsStripAnimationWait(false);
            }
            if (isFullInfoActive) {
                setTrickplayAnimationWait(false);
            }
            if (!isActionsStripActive && isStandardInfoActive && !isFullInfoActive) {
                setActionsStripAnimationWait(false);
            }
        }
        setCurrentTextTrack(oIVideoPlayer.getCurrentTextTrack());
        setCurrentAudioTrackSelected(oIVideoPlayer.getCurrentAudioSelectedTrack());
        if (
            oIVideoPlayer.isTrickplayEnable(playbackState) &&
            (isFullInfoActive || isActionsStripActive)
        ) {
            dispatch(toggleIsPlaying());
        }

        let timer: NodeJS.Timeout;

        if (!isTizenPlatform) {
            timer = setTimeout(() => {
                setStandardInfoAnimationWait(isStandardInfoActive);
                setFullInfoAnimationWait(isFullInfoActive);
                setActionsStripAnimationWait(isActionsStripActive);
                setTrickplayAnimationWait(isTrickplayMenuActive);
            }, INFO_BANNER_HIDE_ANIMATION_TIMEOUT_VALUE);
        }
        return () => {
            clearTimeout(timer);
        };
    }, [isStandardInfoActive, isFullInfoActive, isActionsStripActive, isTrickplayMenuActive]);

    useEffect(() => {
        if (isSpinnerActive && errorCodes) {
            if (oIVideoPlayer.isTrickplayEnable(playbackState)) {
                dispatch(updateTrickPlayMenuTimer());
                VideoManager.getInstance().unPause();
                dispatch(videoPlayerToggleIsPlayingAction(true));
                dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.PLAY));
            }
        }
    }, [isSpinnerActive, errorCodes]);

    useEffect(() => {
        const audioSubtitleOverlayType: boolean =
            uiProperties.overlayType === OverlayTypes.AUDIO_TRACK ||
            uiProperties.overlayType === OverlayTypes.SUBTITLE_AND_CC_LANGUAGE;

        const isAudioSubtitleOverlayActive = isOverlayDialogActive && audioSubtitleOverlayType;

        if (isErrorOSDActive || isErrorOverlayActive) {
            if (isNetworkConnected && oIVideoPlayer.isTrickplayEnable(playbackState))
                dispatch(toggleIsPlaying());
            dispatch(hideInfoBanners());

            if (isAudioSubtitleOverlayActive) {
                dispatch(hideOverlayDialog());
            }

            if (errorPriority !== LIVE_TV_NO_NETWORK_ERROR_PRIORITY) oIVideoPlayer.stopPlayback();
        }
    }, [isErrorOSDActive, isErrorOverlayActive]);

    const infoBannerDiv = (
        <div
            className={`info-banner ${
                isInfobannerContainerActive && !isTizenPlatform ? 'show-info-banner ' : ''
            }
            ${!isInfobannerContainerActive && !isTizenPlatform ? 'hide-info-banner' : ''} `}
            data-testid="info-banner"
        >
            {isStandardInfoActive ||
            standardInfoAnimationWait ||
            (isNetworkConnected && oIVideoPlayer.isTrickplayEnable(playbackState)) ? (
                <StandardInfoBanner
                    mPreviewData={offerPreview}
                    mAiringData={airingOffers}
                    hideBannerTipAtProgramBoundary={hideBannerTipAtProgramBoundary}
                    setHideBannerTipAtProgramBoundary={setHideBannerTipAtProgramBoundary}
                />
            ) : null}
            {isFullInfoActive || fullInfoAnimationWait ? (
                <EventDescription mPreviewData={offerPreview} mAiringData={airingOffers} />
            ) : null}
            {isActionsStripActive || actionsStripAnimationWait ? (
                <ActionsStrip actionItems={actionItems} cursorState={isCursorEnabled} />
            ) : null}
            {isTrickplayMenuActive ||
            trickplayAnimationWait ||
            (isNetworkConnected && oIVideoPlayer.isTrickplayEnable(playbackState)) ? (
                <TrickplayBanner isAdult={isAdult} />
            ) : null}
        </div>
    );

    const spinnerPosition: string =
        fullInfoAnimationWait || trickplayAnimationWait || isFullInfoActive || isTrickplayMenuActive
            ? SpinnerPositionTypes.SPINNER_POSITION_TOP
            : SpinnerPositionTypes.DEFAULT;

    if (!isStandardInfoActive && !isFullInfoActive && isActionsStripActive) {
        osdType = OsdTypes.MINIMIZED_FULL_INFO_BANNER;
    } else if (isTrickplayMenuActive) {
        osdType = OsdTypes.TRICKPLAY_MENU;
    } else if (isFullInfoActive && isAdult) {
        osdType = OsdTypes.FULL_INFO_BANNER_ADULT;
    } else if (isFullInfoActive) {
        osdType = OsdTypes.FULL_INFO_BANNER;
    } else if (isStandardInfoActive) {
        osdType = OsdTypes.STANDARD_INFO_BANNER;
    }

    if (!isNetworkOsdActive && isInBlacklist) {
        // inBlacklist OSD
        const locationError: LiveTvPlaybackError = new LiveTvPlaybackError(
            new PlaybackError(ErrorTypes.LIVE_TV_IN_BLACKLIST_OSD_ERROR_C138),
        );
        showOsd = true;
        osdMessage = locationError.getDescription();
        osdCode = locationError.getUiErrorCode();
        QoEAppLogger.logDeviceBlacklisted(osdCode);
    } else if (!isNetworkOsdActive && isDeviceOutOfRegion) {
        // Out Of Region OSD
        showOsd = true;
        osdMessage = Locale.getString(LocaleKeys.LIVE_TV_OUT_OF_REGION_OSD_MESSAGE);
        osdCode = LIVE_TV_OUT_OF_REGION_OSD_ERROR_CODE;
        QoEAppLogger.logOutOfRegion(osdCode);
    } else if (!isNetworkOsdActive && isDeviceOutOfHome) {
        // Out Of Home OSD
        const locationError: LiveTvPlaybackError = new LiveTvPlaybackError(
            new PlaybackError(ErrorTypes.LIVE_TV_OOH_OSD_ERROR_V404),
        );
        showOsd = true;
        osdMessage = locationError.getDescription();
        osdCode = locationError.getUiErrorCode();
        QoEAppLogger.logOutOfHome(osdCode);
    } else if (!isNetworkOsdActive && isDeviceRestricted) {
        // Can't Play on this device OSD
        showOsd = true;
        osdMessage = Locale.getString(LocaleKeys.LIVE_TV_CANNOT_PLAY_ON_THIS_DEVICE_OSD_MESSAGE);
        osdCode = LIVE_TV_CANNOT_PLAY_ON_THIS_DEVICE_OSD_ERROR_CODE;
        QoEAppLogger.logDeviceRestricted(osdCode);
    } else if (
        !isNetworkOsdActive &&
        isSubscribed &&
        playbackRestrictionType === PlaybackRestrictionType.Adult
    ) {
        // AdultShow OSD
        showOsd = true;
        osdMessage = Locale.getString(LocaleKeys.LIVE_TV_ADULT_OSD_MESSAGE);
        osdCode = EMPTY_STRING;
    } else if (!isNetworkOsdActive && !isSubscribed) {
        // Channel Not Subscribed OSD
        showOsd = true;
        osdMessage =
            name +
            Locale.getString(LocaleKeys.LIVE_TV_CHANNEL_NOT_SUBSCRIBED_FIRST_HALF) +
            services.getBrandingUiInfoForKey(BrandingUiInfoKeys.YOUR_CABLE_PROVIDER) +
            Locale.getString(LocaleKeys.LIVE_TV_CHANNEL_NOT_SUBSCRIBED_SECOND_HALF);
        osdCode = Locale.getString(LocaleKeys.LIVE_TV_CHANNEL_NOT_SUBSCRIBED_ERROR_CODE);
        if (channel && Object.keys(channel).length) {
            QoEAppLogger.logUnsubscribedChannelPlayed();
        }
    } else if (isErrorOSDActive) {
        // Error OSD
        showOsd = true;
        osdMessage = errorMessage;
        osdCode = errorCode;
    }

    // Inactivity OSD
    if (isStillThereOsdActive) {
        showOsd = true;
        osdMessage = Locale.getString(LocaleKeys.LIVE_TV_INACTIVITY_OSD_MESSAGE);
        osdCode = '';
    }

    return (
        <React.Fragment>
            {isCursorEnabled && <RootInteractiveLayer />}
            {showOsd && <Osd message={osdMessage} code={osdCode} type={osdType} />}
            {isSpinnerActive && !showOsd && !oIVideoPlayer.isTrickplayEnable(playbackState) ? (
                <Spinner
                    message={spinnerMessage}
                    position={spinnerPosition}
                    channelNumber={channelNumber}
                    errorCodes={errorCodes}
                    background={true}
                />
            ) : null}
            {isInfobannerContainerActive || infoBannerDivAnimationWait ? infoBannerDiv : null}
            {isVideoDebugOverlayActive ? <VideoDebugOverlay /> : null}
            {isOverrideEnable && <div className="test-mode">TEST MODE</div>}
        </React.Fragment>
    );
};

export default VideoPlayerUI;
